import axios from "axios";

class WorkflowService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl;
  }

  /**
   * @author Edgar Escobedo
   * @description get workflow execution by id
   * @date 11-01-2023
   * @param {String} token
   * @param {uuid} workflowExecutionId
   * @returns {Object}
   * @memberof WorkflowService
   */
  async getWorkFlowExecutionById(workflowExecutionId, token) {
    let response = await axios.get(
      `${this.apiUrl}/workflow-execution-by-id?workflowExecutionId=${workflowExecutionId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
 * @author Edgar Escobedo
 * @description change status workflow by id
 * @date 12-01-2023
 * @param {String} token
 * @param {uuid} workflowExecutionId
 * @param {number} workflowExecutionActionId
 * @returns {Object}
 * @memberof WorkflowService
 */
  async changeStatusWorkflow(workflowExecutionId, workflowExecutionActionId, token, updatedStatusWith) {
    let response = await axios.post(
      `${this.apiUrl}/workflow-execution-engine`,
      { workflowExecutionId, workflowExecutionActionId, updatedStatusWith },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }


  /**
   * @author Brayan Salas
   * @description Method to get team negotiation
   * @param {*} workflowExecutionId
   * @param {*} token
   * @param {boolean} [discussion=false]
   * @return {*} 
   * @memberof WorkflowService
   */
  async getTeamNegotiation(workflowExecutionId, token, discussion = false) {
    let url = `${this.apiUrl}/members-assigned-to-workflow-execution-temporal?workflowExecutionId=${workflowExecutionId}`
    if (discussion) url = url + `&discussion=${discussion}`
    let response = await axios.get(
      url,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data;
  }

  /**
   * @author Brayan Salas
   * @description Method to add member to team negotiation
   * @param {*} workflowExecutionId
   * @param {*} email
   * @param {*} type
   * @param {*} contractorId
   * @param {*} token
   * @return {*} 
   * @memberof WorkflowService
   */
  async addMemberToTeam(workflowExecutionId, email, type, contractorId, token, name, lastName) {
    let body = { workflowExecutionId, email, type }
    if (contractorId >= 0 && type === 'guest') body.contractorId = contractorId
    if (name) body.name = name
    if (lastName) body.lastName = lastName

    let response = await axios.post(
      `${this.apiUrl}/add-member-to-team-temporal`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
    );

    return response.data;
  }

  /**
   * @author Brayan Salas
   * @description Method to delete member from workflow execution
   * @param {*} workflowExecutionId
   * @param {*} userId
   * @param {*} token
   * @return {*} 
   * @memberof WorkflowService
   */
  async deleteMemberFromWorkflowExecution(workflowExecutionId, userId, workflowExecutionMemberTypeId, id, token) {
    let response = await axios.delete(
      `${this.apiUrl}/delete-member-from-workflow-execution-temporal?workflowExecutionId=${workflowExecutionId}&userId=${userId}&workflowExecutionMemberTypeId=${workflowExecutionMemberTypeId}&id=${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Brayan Salas
   * @description Method to generate team negotiation
   * @param {*} workflowExecutionId
   * @param {*} token
   * @return {*} 
   * @memberof WorkflowService
   */
  async generateTemporalTeamNegotiation(workflowExecutionId, token) {
    let response = await axios.post(
      `${this.apiUrl}/create-team-to-workflow-execution-temporal`, { workflowExecutionId }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
    );

    return response.data;
  }

  /**
   * @author Brayan Salas
   * @description Method to generate team negotiation
   * @param {*} workflowExecutionId
   * @param {*} token
   * @return {*} 
   * @memberof WorkflowService
   */
  async generateTeamNegotiation(workflowExecutionId, token) {
    let response = await axios.post(
      `${this.apiUrl}/create-team-to-workflow-execution`, { workflowExecutionId }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
    );

    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description update solicitudeUrl in inbox
  * @date 17-02-2023
  * @param {String} token
  * @param {String} url
  * @returns {Object}
  * @memberof WorkflowService
  */
  async updateSolicitudeUrlInbox(url, idContractor, token) {
    let response = await axios.put(
      `${this.apiUrl}/update-url-contractor`,
      { url, idContractor },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Brayan Salas
  * @description Set member to accept revisions in syncfusion
  * @date 24-02-2023
  * @param {String} email
  * @param {String} workflowExecutionId
  * @returns {Object}
  * @memberof WorkflowService
  */
  async setMemberAcceptRevisions(workflowExecutionId, email, token) {
    let response = await axios.put(
      `${this.apiUrl}/enable-user-track-changes`,
      { workflowExecutionId, email },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Brayan Salas
  * @description Get member can accept revisions in syncfusion
  * @date 24-02-2023
  * @param {String} email
  * @param {String} workflowExecutionId
  * @returns {Object}
  * @memberof WorkflowService
  */
  async getMemberAcceptRevisions(workflowExecutionId, email, token) {
    let response = await axios.get(
      `${this.apiUrl}/user-has-track-changes?email=${email}&workflowExecutionId=${workflowExecutionId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description GET workflow by solicitudeId and workflowId
  * @date 24-02-2023
  * @param {String} solicitudeId
  * @param {Number} workflowId
  * @param {String} token
  * @returns {Object}
  * @memberof WorkflowService
  */
  async getWorkflowBySolicitudeIdAndWorkflowId(solicitudeId, workflowId, token) {
    let response = await axios.get(
      `${this.apiUrl}/workflow-by-solicitudeId?solicitudeId=${solicitudeId}&workflowId=${workflowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description Create workflow REPSE documento with status 0
  * @date 20-04-2023
  * @param {String} token
  * @returns {Object}
  * @memberof WorkflowService
  */
  async createRepseDocWorflow(repseDocument, token) {
    let response = await axios.post(`${this.apiUrl}/repse-workflow`,
      repseDocument,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    return response.data
  }

  /**
  * @author Edgar Escobedo
  * @description  Get repse documents by workflowId
  * @date 20-04-2023
  * @param {String} workflowExecutionId
  * @param {String} token
  * @returns {Object}
  * @memberof WorkflowService
  */
  async getDocumentRepseByWorkflow(workflowExecutionId, token) {
    let response = await axios.get(`${this.apiUrl}/repse-document-workflow?workflowExecutionId=${workflowExecutionId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    return response.data
  }


  /**
  * @author Edgar Escobedo
  * @description GET members form a workflow by workflowExecutionId
  * @updatedDate 21-06-2023
  * @param {String} contractId
  * @returns {Object}
  * @memberof NegotiationCntractService
  */

  async getMembersWorkflow(workflowExecutionId, token) {
    let response = await axios.get(`${this.apiUrl}/members-for-workflow?workflowExecutionId=${workflowExecutionId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    return response.data
  }

  /**
  * @author Edgar Escobedo
  * @description Validate all documents by workflowExecutionId
  * @updatedDate 16-08-2023
  * @param {String} workflowExecutionId
  * @returns {Object}
  * @memberof NegotiationCntractService
  */
  async validateDocumentsRepseByWorkflowExecutionId(workflowExecutionId, token) {
    let response = await axios.post(`${this.apiUrl}/validate-documents-repse`,
      {
        workflowExecutionId
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    return response.data
  }

  /**
  * @author Edgar Escobedo
  * @description 
  * @date 22-08-2023
  * @param {String} token
  * @param {uuid} workflowExecutionId
  * @param {number} action
  * @returns {Object}
  * @memberof WorkflowService
  */
  async updateStatusWorkflow(workflowExecutionId, action, token) {
    let response = await axios.post(
      `${this.apiUrl}/contract-change-status`,
      { workflowExecutionId, action },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description 
  * @date 22-09-2023
  * @param {String} token
  * @param {uuid} workflowExecutionId
  * @param {number} action
  * @returns {Object}
  * @memberof WorkflowService
  */
  async updateStatusWorkflowREPSE(workflowExecutionId, action, documentsRejected, token) {
    let response = await axios.post(
      `${this.apiUrl}/change-status-documents-repse`,
      { workflowExecutionId, action, documentsRejected },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description Change to back status workflow in digital vault follow up registration document
  * @date 22-11-2023
  * @param {String} token
  * @param {uuid} workflowExecutionId
  * @param {number} solicitudeId
  * @returns {Object}
  * @memberof WorkflowService
  */
  async changeBackStatusRegistrationDocument(workflowExecutionId, solicitudeId, token) {
    let response = await axios.post(`${this.apiUrl}/back-status-document`,
      { workflowExecutionId, solicitudeId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description Change to back status workflow in validation signature base
  * @date 02-04-2024
  * @param {String} token
  * @param {uuid} workflowExecutionId
  * @param {String} action
  * @param {number} statusDefined
  * @returns {Object}
  * @memberof WorkflowService
  */
  async changeStatusWorkflowValidationSigantureBase(workflowExecutionId, action, statusDefined, updateSignature, statusSignatureBaseUpdated, token) {
    let response = await axios.post(`${this.apiUrl}/validation-signature-base-workflow`,
      { workflowExecutionId, action, statusDefined, updateSignature, statusSignatureBaseUpdated },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Edgar Escobedo
   * @description GET workflow by solicitudeId and workflowId
   * @date 24-02-2023
   * @param {String} solicitudeId
   * @param {Number} workflowId
   * @param {String} email 
   * @returns {Object}
   * @memberof WorkflowService
   */
  async getWorkflowBySolicitudeIdAndWorkflowIdPublic(solicitudeId, workflowId, email, token) {
    let response = await axios.get(`${this.apiUrl}/workflow-by-solicitudeId-public?solicitudeId=${solicitudeId}&workflowId=${workflowId}&email=${email}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
}

export { WorkflowService as default };